import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import analyticsApi, {
  DashboardAnalytics,
  ExpenseCategoryBreakdown,
  MonthlyTrends,
} from '../../api/analytics';
import FinancialHealthMetricsComponent from '../FinancialHealthMetrics';
import { formatCurrency } from '../../utils/formatters';

export default function AccountingReportsPanel() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardAnalytics | null>(null);
  const [monthlyTrends, setMonthlyTrends] = useState<MonthlyTrends | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategoryBreakdown | null>(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const [dashboardData, trendsData, categoriesData] = await Promise.all([
        analyticsApi.getDashboardAnalytics('30days'),
        analyticsApi.getMonthlyTrends(),
        analyticsApi.getExpenseCategories(),
      ]);
      setDashboard(dashboardData);
      setMonthlyTrends(trendsData);
      setExpenseCategories(categoriesData);
    } catch (error) {
      console.error('Error loading accounting reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Financial Reports</h3>
          <p className="text-sm text-slate-500">Summary for the last 30 days</p>
        </div>
        <Link
          to="/financial-dashboard"
          className="inline-flex items-center rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-500"
        >
          Open Full Dashboard
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm text-slate-500">Revenue</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCurrency(dashboard?.totalRevenue || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <ArrowTrendingDownIcon className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-sm text-slate-500">Expenses</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCurrency(dashboard?.totalExpenses || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <BanknotesIcon className="h-8 w-8 text-indigo-500" />
            <div className="ml-3">
              <p className="text-sm text-slate-500">Net Profit</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCurrency(dashboard?.netProfit || 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm text-slate-500">Profit Margin</p>
              <p className="text-xl font-semibold text-slate-900">
                {(dashboard?.profitMargin || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-base font-medium text-slate-900">Monthly Trends ({monthlyTrends?.year})</h4>
          </div>
          <div className="p-6 space-y-3">
            {monthlyTrends?.trends?.length ? (
              monthlyTrends.trends.slice(-6).map((trend) => (
                <div key={trend.month} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{trend.month}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-green-600">{formatCurrency(trend.sales)}</span>
                    <span className="text-red-600">{formatCurrency(trend.expenses)}</span>
                    <span className={trend.profit >= 0 ? 'text-cyan-600' : 'text-red-600'}>
                      {formatCurrency(trend.profit)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No monthly trend data available.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-base font-medium text-slate-900">Expense Categories</h4>
          </div>
          <div className="p-6 space-y-3">
            {expenseCategories?.categories?.length ? (
              expenseCategories.categories.slice(0, 8).map((category) => (
                <div key={category.category} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{category.category}</span>
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-slate-900">{formatCurrency(category.amount)}</span>
                    <span className="text-slate-500">{category.percentage.toFixed(1)}%</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No expense category data available.</p>
            )}
          </div>
        </div>
      </div>

      <FinancialHealthMetricsComponent />
    </div>
  );
}
